import { IssueAmountStructure } from '@gooduncles/gu-app-schema';
import { ColDef, ColGroupDef, GetDetailRowDataParams, ValueFormatterParams } from 'ag-grid-community';

import { formatDate } from 'src/utils/date-util';
import {
  createCreatorCellRenderer,
  createDeadlineDisabledCellStyle,
  createDeadlineSummaryCellRenderer,
  createInventoryIssueInfoCellRenderer,
  createProductsCellRenderer,
  createStatusCellRenderer,
  createTaskImagesCellRenderer,
  createTaskStatusDateSummaryCellRenderer,
  deadlineOptionValueFormatter,
} from 'src/utils/issue/issue-ag-grid-renderers';
import {
  createDisabledCellStyle,
  externalServiceCellStyle,
  externalServiceCostValueGetter,
  externalServiceNameValueGetter,
  formatAmount,
  partnerCellStyle,
  productsFilterParams,
  registeredAtValueFormatter,
  storeNameTooltipValueGetter,
  taskCategoryCellStyle,
  taskFinishedAtValueFormatter,
  taskFinishedAtValueGetter,
  taskMemoValueGetter,
  taskPartnerNameValueGetter,
  taskTargetStoreValueGetter,
} from 'src/utils/issue/issue-ag-grid-util';
import { issueTaskStatusBadgeMap } from 'src/utils/issue/issue-util';

import { IssueManageDetailRowData, IssueManageMasterRowData } from './IssueManage.types';

const defaultColDef: ColDef = {
  sortable: true,
  resizable: true,
  filter: true,
};

/**
 * 금액 컬럼 그룹
 */
const createAmountColumnGroup = <T extends { amount?: IssueAmountStructure | null }>(): ColGroupDef<T> => ({
  headerName: '금액',
  children: [
    {
      field: 'amount.totalAmount',
      headerName: '총액',
      width: 120,
      valueFormatter: (params: ValueFormatterParams) => formatAmount(params.value),
      cellStyle: { textAlign: 'right', fontWeight: 'bold' },
    },
    {
      field: 'amount.supplyAmount',
      headerName: '공급가',
      width: 120,
      columnGroupShow: 'open',
      valueFormatter: (params: ValueFormatterParams) => formatAmount(params.value),
      cellStyle: { textAlign: 'right' },
    },
    {
      field: 'amount.taxAmount',
      headerName: '세금',
      width: 100,
      columnGroupShow: 'open',
      valueFormatter: (params: ValueFormatterParams) => formatAmount(params.value),
      cellStyle: { textAlign: 'right' },
    },
  ],
});

/**
 * 디테일 그리드 컬럼 정의 (IssueTask)
 */
const detailColumnDefs: (ColDef<IssueManageDetailRowData> | ColGroupDef<IssueManageDetailRowData>)[] = [
  { field: '_id', headerName: 'Task ID', hide: true },
  {
    pinned: 'left',
    field: 'displayCategory',
    headerName: '카테고리',
    width: 120,
    cellStyle: taskCategoryCellStyle,
  },
  {
    headerName: '수행일정',
    children: [
      {
        headerName: '요약',
        width: 120,
        columnGroupShow: 'closed',
        cellRenderer: createDeadlineSummaryCellRenderer<IssueManageDetailRowData>(),
        filter: false,
        sortable: false,
      },
      {
        field: 'targetDate',
        headerName: '적용 날짜',
        width: 110,
        columnGroupShow: 'open',
        valueFormatter: (params: ValueFormatterParams) => (params.value ? formatDate(params.value, 'MM-dd') : '-'),
        cellStyle: { color: '#1890ff', fontWeight: 500 },
      },
      {
        field: 'deadline.option',
        headerName: '유형',
        width: 120,
        columnGroupShow: 'open',
        valueFormatter: deadlineOptionValueFormatter,
        cellStyle: createDeadlineDisabledCellStyle(),
      },
      {
        field: 'deadline.value',
        headerName: '날짜',
        width: 100,
        columnGroupShow: 'open',
        valueFormatter: (params: ValueFormatterParams) => (params.value ? formatDate(params.value, 'MM-dd') : '-'),
        cellStyle: createDeadlineDisabledCellStyle(),
      },
    ],
  },
  {
    headerName: '상태/일시',
    children: [
      {
        headerName: '요약',
        width: 160,
        columnGroupShow: 'closed',
        cellRenderer: createTaskStatusDateSummaryCellRenderer<IssueManageDetailRowData>(issueTaskStatusBadgeMap),
        filter: false,
        sortable: false,
      },
      {
        field: 'status',
        headerName: '상태',
        width: 120,
        columnGroupShow: 'open',
        cellRenderer: createStatusCellRenderer<IssueManageDetailRowData>(issueTaskStatusBadgeMap),
      },
      {
        field: 'registeredAt',
        headerName: '등록일시',
        width: 120,
        columnGroupShow: 'open',
        valueFormatter: registeredAtValueFormatter,
      },
      {
        field: 'finishedAt',
        headerName: '처리일시',
        width: 120,
        columnGroupShow: 'open',
        valueFormatter: registeredAtValueFormatter,
      },
    ],
  },
  {
    hide: true,
    field: 'storeName',
    headerName: '매장명',
    width: 150,
    tooltipValueGetter: storeNameTooltipValueGetter,
  },
  // 금액 컬럼 그룹
  createAmountColumnGroup<IssueManageDetailRowData>(),
  {
    headerName: '이슈 상품',
    width: 200,
    cellRenderer: createProductsCellRenderer<IssueManageDetailRowData>(),
    filter: 'agSetColumnFilter',
    filterParams: productsFilterParams,
  },
  {
    headerName: '메모',
    children: [
      {
        field: 'publicNote',
        headerName: '메모(공개)',
        width: 120,
      },
      {
        field: 'privateNote',
        headerName: '메모(비공개)',
        width: 120,
      },
    ],
  },
  // 파트너 컬럼 그룹
  {
    headerName: '파트너',
    children: [
      {
        headerName: '담당자',
        width: 100,
        valueGetter: taskPartnerNameValueGetter,
        cellStyle: partnerCellStyle,
      },
      {
        headerName: '완료시각',
        width: 180,
        columnGroupShow: 'open',
        valueGetter: taskFinishedAtValueGetter,
        valueFormatter: taskFinishedAtValueFormatter,
        cellStyle: partnerCellStyle,
      },
      {
        headerName: '대상매장',
        width: 120,
        columnGroupShow: 'open',
        valueGetter: taskTargetStoreValueGetter,
        cellStyle: partnerCellStyle,
      },
      {
        headerName: '메모',
        width: 150,
        columnGroupShow: 'open',
        valueGetter: taskMemoValueGetter,
        cellStyle: partnerCellStyle,
      },
    ],
  },
  // 외부 서비스 컬럼 그룹
  {
    headerName: '외부 서비스',
    width: 120,
    children: [
      {
        headerName: '서비스',
        width: 100,
        valueGetter: externalServiceNameValueGetter,
        cellStyle: externalServiceCellStyle,
      },
      {
        headerName: '비용',
        width: 120,
        columnGroupShow: 'open',
        valueGetter: externalServiceCostValueGetter,
        valueFormatter: (params: ValueFormatterParams) => formatAmount(params.value),
        cellStyle: createDisabledCellStyle('externalService'),
      },
    ],
  },
  {
    headerName: '재고처리',
    width: 150,
    cellRenderer: createInventoryIssueInfoCellRenderer<IssueManageDetailRowData>(),
    filter: false,
    sortable: false,
  },
  {
    headerName: '이미지',
    width: 100,
    cellRenderer: createTaskImagesCellRenderer<IssueManageDetailRowData>(),
    filter: false,
    sortable: false,
  },
  {
    headerName: '작성자',
    width: 100,
    cellRenderer: createCreatorCellRenderer<IssueManageDetailRowData>(),
  },
];

/**
 * 디테일 그리드 렌더러 설정
 */
export const detailCellRendererParams = {
  detailGridOptions: {
    columnDefs: detailColumnDefs,
    defaultColDef,
  },
  getDetailRowData: (params: GetDetailRowDataParams<IssueManageMasterRowData>) => {
    params.successCallback(params.data.issueTasks);
  },
};
